import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Typography,
  Grid,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
} from '@mui/material';
import { KeyboardArrowLeft } from '@mui/icons-material';
import { useFormik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { getTimezoneByZipCode } from '../../helpers';
import {
  FHAntdDateTimePicker,
  FBoxContainer,
  FHButton,
  FHInputField,
  FProgressBar,
} from '../../components/base';
import { clientActions } from '../../store';
import { cleanNumericInputValue, disabledFutureDate } from '../../helpers';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY1,
  FORTA_ORANGE,
  FORTA_SUCCESS,
} from '../../utils/colors';
import { trimsValuesInObject } from '../../utils/common';
import PageContainer from '../../components/layout/PageContainer';
import { STANDARD_ERROR_MESSAGES } from '../../constants';
import { DEFAULT_TZ_MESSAGE } from './AddClientForm';
import { RequiresEvvField } from './components';

import type { RootState } from '../../store';
import type { Moment } from 'moment';
import type { SelectChangeEvent } from '@mui/material';
import type { ChangeEvent } from 'react';

function EditClientForm() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { clientId } = useParams();

  const { required } = STANDARD_ERROR_MESSAGES;
  const { selected: client, loading: clientLoading } = useSelector(
    (state: RootState) => state.clients,
  );

  useEffect(() => {
    if (!clientId) return;
    dispatch(clientActions.getClient({ getAccessTokenSilently, clientId }));
    return () => {
      dispatch(clientActions.clearSelectedClient());
    };
  }, [dispatch, getAccessTokenSilently, clientId]);

  const clientValidationSchema = Yup.object().shape({
    first_name: Yup.string().trim().required(required),
    last_name: Yup.string().trim().required(required),
    sex: Yup.string().trim().required(required),
    dob: Yup.string().trim().required(required),
    street_address_line_one: Yup.string().trim().required(required),
    street_address_city: Yup.string().trim().required(required),
    street_address_state: Yup.string().trim().required(required),
    street_address_zip: Yup.string().min(5).max(5).required(),
    evv_required: Yup.boolean().required('Please select Yes or No'),
  });

  const updateClient = async (values: any) => {
    const trimedValues = trimsValuesInObject(values);
    if (values.evv_required === (client?.evv_required ?? false)) {
      delete trimedValues.evv_required;
    }
    try {
      const updateClientResponse = await dispatch(
        clientActions.updateClient({
          data: trimedValues,
          clientId,
          getAccessTokenSilently,
        }),
      );
      if (updateClientResponse?.error) {
        toast.error(updateClientResponse.error);
      }

      return updateClientResponse?.payload?.id;
    } catch {
      toast.error('An error occurred while creating client');
      return null;
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: client?.first_name || '',
      last_name: client?.last_name || '',
      middle_name: client?.middle_name || '',
      dob: client?.dob || '',
      sex: client?.sex || '',
      street_address_line_one: client?.street_address_line_one || '',
      street_address_line_two: client?.street_address_line_two || '',
      street_address_city: client?.street_address_city || '',
      street_address_state: client?.street_address_state || '',
      street_address_zip: client?.street_address_zip || '',
      evv_required: client?.evv_required ?? false,
    },

    onSubmit: async (values) => {
      const clientID = await updateClient(values);

      if (clientID) {
        formik.resetForm();
        dispatch(
          clientActions.getList({
            getAccessTokenSilently,
          }),
        );
      }
    },
    validationSchema: clientValidationSchema,
  });
  const { values, setFieldValue, submitForm, errors, touched } = formik;

  const handleUpdateFieldValues = (key: string, value: any) => {
    const formattedValue = value;
    setFieldValue(key, formattedValue);
  };

  let zone = DEFAULT_TZ_MESSAGE;
  const identifiedTz = getTimezoneByZipCode(values['street_address_zip']);
  if (identifiedTz) {
    zone = identifiedTz;
  }

  if (clientLoading) {
    return <FProgressBar />;
  }
  return (
    <PageContainer>
      {clientLoading && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Link to={`/admin/client-details-page/${clientId}`}>
            <KeyboardArrowLeft sx={{ height: '21px', width: '21px', mb: -0.7 }} />
            Back to client page
          </Link>
        </Box>
        <FBoxContainer sx={{ borderRadius: '20px' }}>
          <form
            data-testid="edit-clientForm"
            onSubmit={formik.handleSubmit}
            style={{ width: '100%' }}
          >
            <Grid container>
              <Grid item xs={12} md={12} lg={8}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box
                    ml={1}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexGrow: 1,
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography textAlign="left" variant="h4">
                      Edit Client
                    </Typography>
                    <Typography fontSize={14} textAlign="right" alignSelf={'flex-end'}>
                      <span style={{ color: 'red', fontSize: 16 }}>*</span>indicates mandatory
                      fields
                    </Typography>
                  </Box>
                  <Box
                    sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                  />
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        First Name
                        <span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientFirstName',
                          }
                        }
                        style={{ width: '100%' }}
                        value={values['first_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('first_name', e.target.value)
                        }
                        error={touched.first_name && Boolean(errors.first_name)}
                        helperText={touched.first_name && errors.first_name}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        Last Name<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientLastName',
                          }
                        }
                        style={{ width: '100%' }}
                        value={values['last_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('last_name', e.target.value)
                        }
                        error={touched.last_name && Boolean(errors.last_name)}
                        helperText={touched.last_name && errors.last_name}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>Middle Name</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientMiddleName',
                          }
                        }
                        style={{ width: '100%' }}
                        value={values['middle_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('middle_name', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        DOB<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHAntdDateTimePicker
                        inputHeight={40}
                        data-testid="clientDOB"
                        format="MM/DD/YYYY"
                        style={{ width: '100%' }}
                        disabledDate={disabledFutureDate}
                        error={touched.dob && Boolean(errors.dob)}
                        helperText={<span>{touched.dob && required}</span>}
                        allowClear={false}
                        value={values['dob'] ? moment(values['dob']) : null}
                        onSelect={(value: Moment) =>
                          handleUpdateFieldValues('dob', value.format('YYYY-MM-DD'))
                        }
                        onChange={(value: Moment) =>
                          handleUpdateFieldValues('dob', value.format('YYYY-MM-DD'))
                        }
                      />
                      {touched.dob && Boolean(errors.dob) && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, fontSize: '14px', textAlign: 'right' }}
                        >
                          {errors.dob}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        Gender<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <RadioGroup
                        data-testid="clientGender"
                        row
                        value={values['sex']}
                        name="gender-radio-buttons-group"
                        onChange={(e: SelectChangeEvent<unknown>) =>
                          handleUpdateFieldValues('sex', e.target.value)
                        }
                      >
                        <FormControlLabel
                          data-testid="clientGenderMale"
                          value={'male'}
                          control={<Radio />}
                          label="Male"
                        />
                        <FormControlLabel value={'female'} control={<Radio />} label="Female" />
                      </RadioGroup>
                      {touched.sex && Boolean(errors.sex) && (
                        <FormHelperText sx={{ color: 'red', fontSize: '0.75rem' }}>
                          {errors.sex}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        Street Address 1<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientStreetAddress1',
                          }
                        }
                        style={{ width: '100%' }}
                        value={values['street_address_line_one']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_line_one', e.target.value)
                        }
                        error={
                          touched.street_address_line_one && Boolean(errors.street_address_line_one)
                        }
                        helperText={
                          touched.street_address_line_one &&
                          errors.street_address_line_one
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>Street Address 2</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientStreetAddress2',
                          }
                        }
                        style={{ width: '100%' }}
                        value={values['street_address_line_two']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_line_two', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        City<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientCity',
                          }
                        }
                        style={{ width: '100%' }}
                        value={values['street_address_city']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_city', e.target.value)
                        }
                        error={touched.street_address_city && Boolean(errors.street_address_city)}
                        helperText={
                          touched.street_address_city && errors.street_address_city
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        State<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'clientState',
                          }
                        }
                        style={{ width: '100%' }}
                        value={values['street_address_state']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_state', e.target.value)
                        }
                        error={touched.street_address_state && Boolean(errors.street_address_state)}
                        helperText={
                          touched.street_address_state && errors.street_address_state
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} display={'flex'} alignItems={'center'}>
                    <Grid item xs={2.5}>
                      <Typography textAlign={'left'}>
                        Zip<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Box sx={{ width: '100%', display: 'flex' }}>
                        <FHInputField
                          inputHeight={40}
                          inputProps={
                            {
                              'data-testid': 'clientZip',
                          }
                          }
                          fullWidth
                          sx={{ width: '100%', borderRadius: '10px 0 0 10px' }}
                          value={values['street_address_zip']}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleUpdateFieldValues(
                              'street_address_zip',
                              cleanNumericInputValue(e.target.value),
                            )
                          }
                          error={touched.street_address_zip && Boolean(errors.street_address_zip)}
                          helperText={
                            touched.street_address_zip && errors.street_address_zip
                          }
                        />
                        <Box
                          sx={{
                            height: 40,
                            width: '100%',
                            backgroundColor: FORTA_GRAY1,
                            borderRadius: '0 10px 10px 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'not-allowed',
                            borderLeft: `1px solid ${FORTA_BLUE.primary}`,
                          }}
                        >
                          {zone === DEFAULT_TZ_MESSAGE && values['street_address_zip'] && (
                            <WarningAmberRoundedIcon sx={{ color: FORTA_ORANGE, ml: 2 }} />
                          )}
                          {zone !== DEFAULT_TZ_MESSAGE && (
                            <CheckCircleRoundedIcon sx={{ color: FORTA_SUCCESS, ml: 2 }} />
                          )}
                          <Typography ml={1}>{zone.replaceAll('_', ' ')}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <RequiresEvvField formik={formik} />
                </Box>

                <Box ml={1} mt={3} sx={{ display: 'flex' }}>
                  <FHButton
                    data-testid="clientSubmit"
                    onClick={submitForm}
                    disabled={formik.isSubmitting}
                  >
                    Submit
                  </FHButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default EditClientForm;
